import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Delete, ModeEdit } from '@mui/icons-material';
import { Box, CircularProgress, SpeedDialAction } from '@mui/material';
import { observer } from 'mobx-react-lite';
import {
  AdjustmentsList,
  AdvancesList,
  AssetsList,
  ConfirmModal,
  Documents,
  FuseTabs,
  LendersList,
  LoanInformation,
  Notes,
  PaymentsList,
  RatesAndFees,
  ViewPage,
} from '/src/components';
import { useLoanStore, useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType, LoanStatus } from '/src/lib/types';
import { theme } from '/src/theme';

/**
 * View Loan Page
 */
export const ViewLoanPage = observer(() => {
  /** Context **/
  const loanStore = useLoanStore();
  const userStore = useUserStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [showConfirmDeactivate, setShowConfirmDeactivate] = useState(false);

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    true, // Loan Details
    false, // Rates & Fees
    false, // Company Comments
    false, // Socket Comments
    false, // Documents
  ]);

  /** Computed **/
  const loan = loanStore.currentLoan;
  const assetListOptions = {
    notes: true,
    name: true,
    originalValue: true,
    currentValue: true,
    status: true,
  };

  /** Methods **/

  const deactivate = async () => {
    if (!loan) return;

    try {
      await loanStore.deactivate(loan?._id);

      // Show alert
      toast.success(toastMessages.deleteLoan.success);

      // Redirect to assets
      navigate('/deals');
    } catch (err) {
      console.error(err);
      toast.error(toastMessages.deleteLoan.error);
    }
  };

  /**
   * Updates the Company Comments list.
   *
   * @remarks
   * This works the same as any other Notes field. We just needed two of them.
   */
  const updateCompanyComments = async (companyComments: Note[]) => {
    if (!loan) return;

    const response = await loanStore.update({ ...loan, companyComments });
    if (!response) throw new Error(`Error updating company comments for Asset ID ${loan._id}`);

    loanStore.currentLoan = await loanStore.get(loan._id);
  };

  /**
   * Update Socket Comments
   *
   * @remarks
   * This works the same as any other Notes field. We just needed two of them.
   */
  const updateSocketComments = async (socketComments: Note[]) => {
    if (!loan) return;

    const response = await loanStore.update({ ...loan, socketComments });
    if (!response) throw new Error(`Error updating socket comments for Asset ID ${loan._id}`);

    loanStore.currentLoan = await loanStore.get(loan._id);
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!loan) return;

    const response = await loanStore.update({ ...loan, files });
    if (!response) throw new Error(`Error updating files for Asset ID ${loan._id}`);

    loanStore.currentLoan = await loanStore.get(loan._id);
  };

  /** Effects **/

  useEffect(() => {
    if (!params.loanId) return;

    Promise.all([loanStore.get(params.loanId)]);
  }, [params.loanId]);

  /** Render **/
  return (
    <ViewPage
      modelName={loan?.name ?? ''}
      modelType={DataModelType.Loan}
      sidebarFlex="25"
      contentFlex="75"
      sidebar={
        <>
          <LoanInformation
            loan={loan}
            open={sidebarItems[0]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(0, !sidebarItems[0]))}
          />
          <RatesAndFees
            loan={loan}
            open={sidebarItems[1]}
            toggleOpen={() => setSidebarItems(sidebarItems.with(1, !sidebarItems[1]))}
          />
          <Notes
            mode={CRUDMode.View}
            notes={loan?.companyComments ?? []}
            title="Notes"
            tooltip="Notes"
            sidebarIndex={2}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateNotes={updateCompanyComments}
          />
          {userStore.isAdmin && (
            <Notes
              mode={CRUDMode.View}
              notes={loan?.socketComments ?? []}
              title="Internal Notes"
              tooltip="Internal Notes"
              sidebarIndex={3}
              sidebarItems={sidebarItems}
              setSidebarItems={setSidebarItems}
              updateNotes={updateSocketComments}
            />
          )}
          <Documents
            mode={CRUDMode.Edit}
            documents={loan?.files ?? []}
            sidebarIndex={4}
            sidebarItems={sidebarItems}
            setSidebarItems={setSidebarItems}
            updateDocuments={updateFiles}
          />
        </>
      }
      speedDialActions={[
        <SpeedDialAction
          key="edit-button"
          className="md-fab-action-item"
          icon={<ModeEdit className="material-icons" />}
          slotProps={{
            fab: {
              'aria-label': 'Edit Deal',
              className: 'md-fab md-mini md-accent md-button md-default-theme',
            },
            tooltip: {
              title: 'Edit Deal',
            },
          }}
          onClick={() => navigate(`/deals/${loan?._id}/edit`)}
        />,
        <SpeedDialAction
          hidden={loan?.status !== LoanStatus.Active}
          key="delete-button"
          className="md-fab-action-item md-warn"
          icon={<Delete className="material-icons" />}
          slotProps={{
            fab: {
              'aria-label': 'Deactivate Deal',
              className: 'md-fab md-mini md-warn md-button md-default-theme',
              style: { background: theme.palette.warning.main, color: 'white' },
            },
            tooltip: {
              title: 'Deactivate Deal',
            },
          }}
          onClick={() => (loan?.status === LoanStatus.Active ? setShowConfirmDeactivate(true) : {})}
        />,
      ]}
      modals={
        <ConfirmModal
          content="Are you sure you want to deactivate this Deal? Doing so will deactivate its associated loans and assets as well."
          open={showConfirmDeactivate}
          setOpen={setShowConfirmDeactivate}
          onOk={() => deactivate()}
          label="Confirm Deal Deactivation"
          okLabel="Deactivate"
          cancelLabel="Cancel"
        />
      }
    >
      {loan ? (
        <FuseTabs defaultTab={0}>
          {[
            {
              index: 0,
              label: 'Advances',
              content: loan ? <AdvancesList query={{ loanId: loan._id }} /> : <></>,
            },
            {
              index: 1,
              label: 'Payments',
              content: loan ? <PaymentsList query={{ loanId: loan._id }} /> : <></>,
            },
            {
              index: 2,
              label: 'Assets',
              content: loan ? <AssetsList query={{ loanId: loan._id }} viewOptions={assetListOptions} /> : <></>,
            },
            {
              index: 3,
              label: 'Adjustments',
              content: loan ? <AdjustmentsList query={{ loanId: loan._id }} /> : <></>,
            },
            {
              index: 4,
              label: 'Investors',
              content: loan ? <LendersList data={loan.lenders} /> : <></>,
            },
          ]}
        </FuseTabs>
      ) : (
        <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
          <CircularProgress className="md-primary" md-mode="indeterminate" />
        </Box>
      )}
    </ViewPage>
  );
});
